<template>
  <transition
    enter-class="cxm-NewToast-Transition-LeaveTo"
    enter-active-class="cxm-NewToast-Transition-EnterActive"
    leave-active-class="cxm-NewToast-Transition-LeaveActive"
    leave-to-class="cxm-NewToast-Transition-LeaveTo"
  >
    <div
      v-if="show"
      class="cxm-NewToast"
      :class="baseClass"
      :style="style"
      @mouseover="handleMouseoverPause"
      @mouseleave="handleMouseleaveContinue"
    >
      <div class="cxm-NewToast-Header">
        <div class="cxm-NewToast-Title">
          <Icon
            v-if="!isSuccess"
            name="warningOutlined"
            color="var(--track-color-negative-medium)"
            size="26"
            outlined
          />
          <Icon
            v-else
            name="checkOutlined"
            color="var(--track-color-positive-medium)"
            size="26"
            outlined
          />
          <span>
            {{ getTitle }}
          </span>
        </div>
        <div class="cxm-NewToast-Close">
          <Icon
            name="close"
            color="var(--track-color-bluegray-80)"
            size="24"
            :handle-click="handleHide"
          />
        </div>
      </div>
      <p class="cxm-NewToast-Message">
        {{ message }}
      </p>
      <div
        v-if="!isSuccess"
        class="cxm-NewToast-Error"
        @click.stop="handleCopy"
      >
        <div class="cxm-NewToast-CopyMessage">
          {{ getCopyMessage }}
        </div>

        <div
          class="cxm-NewToast-CopyError"
          :class="{ '-enable': !dataCopied, '-disable': dataCopied }"
        >
          <div>
            <input
              ref="refErrorData"
              type="hidden"
              :value="getErrorData"
            >
            <div>
              <Icon
                v-if="!dataCopied"
                :name="copyIconStyle.icon"
                :color="copyIconStyle.color"
                :size="copyIconStyle.size"
              />
              <transition
                mode="out-in"
                enter-class="cxm-NewToast-Transition-ButtonLeaveTo"
                enter-active-class="cxm-NewToast-Transition-ButtonEnterLeaveActive"
                leave-active-class="cxm-NewToast-Transition-ButtonEnterLeaveActive"
                leave-to-class="cxm-NewToast-Transition-ButtonLeaveTo"
              >
                <Icon
                  v-if="dataCopied"
                  :name="copyIconStyle.icon"
                  :color="copyIconStyle.color"
                  :size="copyIconStyle.size"
                />
              </transition>
            </div>
          </div>
          <caption class="cxm-NewToast-ButtonCopyError">
            {{ !dataCopied ? getCopyButtonLabel : getSuccessButtonLabel }}
          </caption>
        </div>
        <div />
      </div>
    </div>
  </transition>
</template>

<script>
import { Icon } from '@/packages/ui/components';
import { mapActions } from 'vuex';

export default {
  components: { Icon },
  props: {
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    copyMessage: {
      type: String,
      default: '',
    },
    copyButtonErrorLabel: {
      type: String,
      default: '',
    },
    successButtonLabel: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'error',
      validator: (v) => ['success', 'error'].includes(v),
    },
    width: {
      type: String,
      default: '460px',
    },
    timeout: {
      type: Number,
      default: 4000,
    },
    errorData: {
      type: Object,
      default: () => ({ transactionId: '', message: '' }),
    },
  },
  data() {
    return {
      interval: null,
      dataCopied: false,
      counter: 0,
      elapsedTime: this.timeout,
      progress: Number(this.width.replace(/[^0-9]+/g, '')) * -1,
    };
  },
  computed: {
    getTitle() {
      return this.title || this.$t('title');
    },
    getCopyMessage() {
      return this.copyMessage || this.$t('copyMessage');
    },
    getCopyButtonLabel() {
      return this.copyButtonErrorLabel || this.$t('copyButtonErrorLabel');
    },
    getSuccessButtonLabel() {
      return this.successButtonLabel || this.$t('successButtonLabel');
    },
    isSuccess() {
      return this.type === 'success';
    },
    baseClass() {
      return {
        [this.isSuccess ? '-success' : '-error']: true,
      };
    },
    style() {
      return {
        width: this.width,
        'background-position-x': `${this.progress}px`,
        '--width': this.width,
        '--timeout': `${this.elapsedTime / 1000}s`,
      };
    },
    getErrorData() {
      return JSON.stringify({ ...this.errorData });
    },
    copyIconStyle() {
      return {
        icon: this.dataCopied ? 'check' : 'copy',
        size: this.dataCopied ? '16' : '16',
        color: this.dataCopied ? 'var(--track-color-positive-darkest)' : 'var(--track-color-bluegray-80)',
      };
    },
  },
  watch: {
    show(value) {
      if (value === false) {
        this.interval = null;
        this.dataCopied = false;
        this.counter = 0;
        this.elapsedTime = this.timeout;
        this.progress = Number(this.width.replace(/[^0-9]+/g, '')) * -1;
      }
    },
  },
  mounted() {
    this.setProgress();
  },
  methods: {
    ...mapActions({
      hideToast: 'ui/hideToast',
    }),
    handleHide() {
      clearInterval(this.interval);
      this.hideToast();
    },
    handleMouseoverPause() {
      clearInterval(this.interval);
      const progressBar = document.getElementsByClassName('cxm-NewToast')[0];
      const computedStyle = window.getComputedStyle(progressBar);
      const marginLeft = computedStyle.getPropertyValue('background-position-x');
      progressBar.classList.remove('cxm-NewToast-Transition-EnterActive');
      progressBar.style['background-position-x'] = marginLeft;
    },
    handleMouseleaveContinue() {
      const progressBar = document.getElementsByClassName('cxm-NewToast')[0];
      progressBar.classList.add('cxm-NewToast-Transition-EnterActive');
      progressBar.style['background-position-x'] = '0px';
      this.setProgress();
    },
    handleCopy() {
      if (this.dataCopied) {
        return;
      }
      const codeToCopy = this.$refs.refErrorData;
      codeToCopy.setAttribute('readOnly', true);
      codeToCopy.setAttribute('type', 'text');
      codeToCopy.select();
      document.execCommand('copy');
      codeToCopy.setAttribute('type', 'hidden');
      this.dataCopied = true;
    },
    setProgress() {
      if (this.counter > 0) {
        this.counter -= 1;
        this.elapsedTime = ((this.timeout / 1000) - this.counter) * 1000;
        this.elapsedTime = this.elapsedTime < 0 ? 1000 : this.elapsedTime;
      }
      this.interval = setInterval(() => {
        if (this.progress < 0) {
          this.progress = 0;
        }
        if (this.counter >= (this.elapsedTime / 1000)) {
          this.handleHide();
        }
        this.counter += 1;
      }, 1000);
    },
  },
  i18n: {
    messages: {
      pt_BR: {
        title: 'Encontramos um problema',
        copyMessage: 'Ao copiar, serão coletados dados complementares',
        copyButtonErrorLabel: 'Copiar erro',
        successButtonLabel: 'Copiado!',
      },
      en_US: {
        title: 'Problem found',
        copyMessage: 'When copying, additional data will be collected',
        copyButtonErrorLabel: 'Copy error',
        successButtonLabel: 'Copied!',
      },
      es_ES: {
        title: 'Encontramos un problema',
        copyMessage: 'Al copiar, se recopilarán datos adicionales',
        copyButtonErrorLabel: 'Copiar error',
        successButtonLabel: 'Copiado!',
      },
    },
  },
};
</script>
<style lang="scss" scoped>
$width: var(--width);
$nWidth: calc(var(--width) * -1);

.cxm-NewToast {
  position: fixed;
  top: 70px;
  right: 50px;
  z-index: 100001;
  background: var(--track-color-white);
  padding: var(--track-spacing-md);
  box-shadow: var(--track-shadow-lg);
  border-radius: var(--track-border-radius-sm);

  &-Header {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--track-spacing-sm);
  }

  &-Title {
    display: flex;
    align-items: center;
    line-height: 22.4px;
    font-size: var(--track-font-size-sm);
    font-weight: var(--track-font-weight-medium);
    gap: var(--track-spacing-xs);
  }

  &-Message {
    line-height: 19.6px;
    color: var(--track-color-bluegray-80);
    font-weight: var(--track-font-weight-regular);
    font-size: var(--track-font-size-xs);
    margin-bottom: var(--track-spacing-sm);
  }

  &-Error {
    display: flex;
    align-items: center;
    background: #F0F3F6;
    border: var(--track-border-sm) solid var(--track-color-bluegray-20);
    border-radius: var(--track-border-radius-md);
    padding: var(--track-spacing-xs)
              var(--track-spacing-xs)
              var(--track-spacing-xs)
              var(--track-spacing-sm);
    color: var(--track-color-bluegray-80);
  }

  &-CopyMessage {
    flex-grow: 1;
    font-style: italic;
    font-family: 'Inter';
    line-height: 16.8px;
    color: var(--track-color-bluegray-60);
    font-weight: var(--track-font-weight-medium);
    font-size: var(--track-font-size-xxs);
  }

  &-CopyError {
    display: flex;
    align-items: center;
    min-width: 97px;
    gap: var(--track-spacing-sm);
    justify-content: center;
    padding: var(--track-spacing-xs);
    border-radius: var(--track-border-radius-sm);
    border: var(--track-border-sm) solid var(--track-color-bluegray-20);
    font-size: var(--track-font-size-xxs);
    background: var(--track-color-bluegray-20);

    &.-enable:hover {
      cursor: pointer;
      border: var(--track-border-sm) solid var(--track-color-bluegray-30);
      color: var(--track-color-bluegray-80);
    }
    &.-disable {
      border: var(--track-border-sm) solid var(--track-color-positive-medium);
      background: var(--track-color-positive-lightest);
      color: var(--track-color-positive-darkest);
    }
  }

  &-ButtonCopyError {
    align-self: center;
    line-height: 16.8px;
    font-size: var(--track-font-size-xxs);
    font-weight: var(--track-font-weight-medium);
  }

  &-Close {
    cursor: pointer;
    height: 26px;
    width: 26px;

    &:hover {
      box-shadow: var(--track-shadow-lg);
      background: var(--track-color-bluegray-10);
      border-radius: var(--track-border-radius-full);
    }
  }

  &.-error, &.-success {
    border-top: double 6px transparent;
    background-origin: border-box;
    background-clip: padding-box, border-box;
    background-position-x: $nWidth;
    background-size: $width 6px;
    background-repeat: no-repeat;
  }

  &.-success {
    color: var(--track-color-positive-darkest);
    background-image: linear-gradient(white, white),
                      radial-gradient(circle at top left,
                        var(--track-color-positive-darkest),
                        var(--track-color-positive-darkest));
  }
  &.-error {
    color: var(--track-color-negative-darkest);
    background-image: linear-gradient(white, white),
                      radial-gradient(circle at top left,
                        var(--track-color-negative-darkest),
                        var(--track-color-negative-darkest));
  }

  &-Transition {
    &-EnterActive {
      transition: transform 0.9s ease,
                    opacity 0.9s ease,
                    background-position-x var(--timeout) ease-out;
    }

    &-LeaveActive {
      transition: transform 0.3s ease,
                    opacity 0.3s ease,
                    background-position-x var(--timeout) ease-out;
    }

    &-LeaveTo {
      transform: translateY(-100px);
      opacity: 0;
    }

    &-ButtonEnterLeaveActive {
      opacity: 0.5;
      transition: opacity 0.5s;
    }
    &-ButtonLeaveTo {
      opacity: 0;
    }
  }
}
</style>
